/**
 * EternityPortal — The full composition.
 *
 * Frame (shared, gold-on-black) + Portrait + Halo (pineal gland) + Portal Effect (per-icon).
 * This is the "Window of Eternity" — the portal through which a life becomes present.
 */

import { EternityFrame } from './EternityFrame';
import { EternityPortrait } from './EternityPortrait';
import { PortalEffect } from './PortalEffect';
import type { HaloConfig } from '../engine/guilloche';
import type { PortalEffectConfig } from '../effects/types';

interface EternityPortalProps {
  /** Portrait image URL */
  imageSrc: string;
  /** Subject name */
  name: string;
  /** Subtitle (e.g. Greek name) */
  subtitle?: string;
  /** Link destination */
  href?: string;

  /** Portal width (inner content) */
  width: number;
  /** Aspect ratio */
  heightRatio?: number;

  /** Arch shape */
  archPointedness?: number;
  archStart?: number;
  frameThickness?: number;

  /** Halo */
  haloConfig: HaloConfig;
  pinealGland: { x: number; y: number };
  haloTranslate?: { x: number; y: number };
  haloScale?: number;

  /** Portrait positioning */
  imageScale?: number;
  imageOffsetX?: number;
  fadeStart?: number;

  /** Portal particles */
  portalEffect?: PortalEffectConfig;

  alt?: string;
}

export function EternityPortal({
  imageSrc,
  name,
  subtitle,
  href,
  width,
  heightRatio = 1.45,
  archPointedness = 0.22,
  archStart = 0.33,
  frameThickness = 14,
  haloConfig,
  pinealGland,
  haloTranslate,
  haloScale = 1.1,
  imageScale = 0.7,
  imageOffsetX = 0,
  fadeStart = 0.68,
  portalEffect,
  alt,
}: EternityPortalProps) {
  const h = width * heightRatio;

  const content = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textDecoration: 'none',
        color: 'inherit',
        cursor: href ? 'pointer' : undefined,
      }}
    >
      <EternityFrame
        width={width}
        heightRatio={heightRatio}
        archPointedness={archPointedness}
        archStart={archStart}
        frameThickness={frameThickness}
      >
        <EternityPortrait
          imageSrc={imageSrc}
          alt={alt ?? name}
          width={width}
          heightRatio={heightRatio}
          pinealGland={pinealGland}
          haloTranslate={haloTranslate}
          haloScale={haloScale}
          haloConfig={haloConfig}
          fadeStart={fadeStart}
          imageScale={imageScale}
          imageOffsetX={imageOffsetX}
        >
          {/* Portal particles — inside the frame, above portrait */}
          {portalEffect && (
            <PortalEffect
              width={width}
              height={h}
              config={portalEffect}
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 4,
              }}
            />
          )}
        </EternityPortrait>
      </EternityFrame>

      {/* Name + subtitle below the frame */}
      <div style={{ textAlign: 'center', marginTop: '1.5rem', pointerEvents: 'none' }}>
        <div
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)',
            fontWeight: 400,
            color: '#F5F0E8',
            letterSpacing: '0.06em',
            lineHeight: 1.2,
            marginBottom: '0.4rem',
          }}
        >
          {name}
        </div>
        {subtitle && (
          <div
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: 'clamp(0.85rem, 1.5vw, 1.05rem)',
              fontWeight: 400,
              color: '#C5A258',
              letterSpacing: '0.1em',
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );

  if (href) {
    return (
      <a href={href} style={{ textDecoration: 'none', color: 'inherit' }}>
        {content}
      </a>
    );
  }

  return content;
}

export default EternityPortal;
